import { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  Paper,
  Button,
  CircularProgress
} from "@mui/material";
import { toast } from "react-toastify";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import {
  useGetFormTypesQuery,
  useLazyGetTaxFormQuery
} from "../../../services/API/reportApiSlice";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Navigate } from "react-router-dom";

const startedYear = 2023;
const currentYear = new Date().getFullYear() - 1;
const countryCode = "US";

// Reusable helper
function downloadPdfFromBase64(base64Content: string, fileName: string) {
  const byteCharacters = atob(base64Content);
  const byteArray = new Uint8Array(
    Array.from(byteCharacters, char => char.charCodeAt(0))
  );

  const blob = new Blob([byteArray], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
}

export default function TaxForms() {
  const [year, setYear] = useState<number>(currentYear);
  const [formType, setFormType] = useState<string>("US1099k");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const accountHolderId = localStorage.getItem("accountHolderId") ?? "";
  const countryCode = localStorage.getItem("countryCode") ?? "";

  // Get Launch Darkly Flags
  const flags = useFlags();

  let isTaxFormsEnabledForDealer = false;

  if (countryCode === "US") {
    isTaxFormsEnabledForDealer = flags.isTaxFormsEnabledForDealer;
  }

  useEffect(() => {
    document.title = "Tax Forms";
  }, []);

  const { data: formTypes = [], isFetching } = useGetFormTypesQuery(
    countryCode
  );
  const [triggerGetTaxForm] = useLazyGetTaxFormQuery();

  // Generate years list from current year -> started year
  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = currentYear; y >= startedYear; y--) {
      list.push(y);
    }
    return list;
  }, []);

  // Submit handler
  const handleSubmit = async () => {
    if (!accountHolderId) {
      toast.error("Account Holder ID not found.");
      return;
    }

    setIsProcessing(true);

    try {
      const request = { accountHolderId, formType, year };
      const { data: result } = await triggerGetTaxForm(request);

      if (!result) {
        toast.error("Unexpected server response.");
        return;
      }

      if (result.isSuccess && result.base64Content) {
        const fileName = `${formType}_form_${year}.pdf`;

        downloadPdfFromBase64(result.base64Content, fileName);

        toast.success("Tax form downloaded successfully!");
        return;
      }

      // Error handling
      if (result.statusCode === 404 || result.statusCode === 400) {
        toast.error(
          `Your ${formType} report for ${year} is not yet available. Please check back later.`
        );
      } else {
        toast.error(result.message || "Failed to get Tax Form");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {isTaxFormsEnabledForDealer === false && <Navigate to="/401" />}
      {isTaxFormsEnabledForDealer === true && (
        <>
          <Box>
            {isFetching ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{ padding: "0px", paddingRight: "0px" }}
              >
                <Box>
                  Please wait. Processing.....
                  <CircularProgress size={25}></CircularProgress>{" "}
                </Box>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Paper>
                    <Box
                      style={{
                        padding: "5px",
                        fontSize: "18px",
                        color: "#3366ff",
                        borderBottom: "1px solid #EAEDED",
                        marginBottom: "20px"
                      }}
                    >
                      Tax Forms
                    </Box>

                    {/* Year Dropdown */}
                    <Box
                      style={{ margin: "20px 5px 0px 10px", fontSize: "small" }}
                    >
                      <FormControl fullWidth size="small">
                        <InputLabel>Tax Year</InputLabel>
                        <Select
                          value={year.toString()}
                          label="Tax Year"
                          style={{ width: 565, marginTop: "10px" }}
                          onChange={(e: SelectChangeEvent) =>
                            setYear(Number(e.target.value))
                          }
                        >
                          {years.map(y => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Form Type Dropdown */}
                    <Box
                      style={{ margin: "20px 5px 0px 10px", fontSize: "small" }}
                    >
                      <FormControl fullWidth size="small">
                        <InputLabel>Form Type</InputLabel>
                        <Select
                          value={formType}
                          label="Tax Year"
                          style={{ width: 565, marginTop: "10px" }}
                          onChange={(e: SelectChangeEvent) =>
                            setFormType(e.target.value)
                          }
                        >
                          {formTypes.map(ft => (
                            <MenuItem key={ft.formType} value={ft.formType}>
                              {ft.formType}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Generate Button */}
                    <Box style={{ margin: "10px 0px 10px 10px" }}>
                      <table>
                        <tbody>
                          <tr>
                            <td>
                              <Button
                                disabled={isProcessing}
                                style={formStyles.fieldSpacer}
                                variant="contained"
                                onClick={handleSubmit}
                              >
                                Generate
                              </Button>
                            </td>
                            <td>
                              {isProcessing && (
                                <CircularProgress
                                  size={25}
                                  style={{
                                    marginLeft: "15px",
                                    marginTop: "20px"
                                  }}
                                  color="info"
                                />
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  </Paper>
                </Box>
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
}
